import React, { useState, useEffect, useCallback } from 'react';

export default function AdminDiscordConfig({ onLogout }) {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [savedState, setSavedState] = useState({ webhookUrl: '', enabled: false });

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');
      if (!token) { onLogout(); return; }

      const response = await fetch('/api/admin?action=get-discord-config', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) { onLogout(); return; }

      const data = await response.json();
      if (data.success) {
        const cfg = data.config;
        setWebhookUrl(cfg.kanbanWebhookUrl || '');
        setEnabled(!!cfg.kanbanNotificationsEnabled);
        setSavedState({
          webhookUrl: cfg.kanbanWebhookUrl || '',
          enabled: !!cfg.kanbanNotificationsEnabled
        });
        setHasChanges(false);
      } else {
        setError(data.error || 'Failed to load Discord config');
      }
    } catch (err) {
      setError('Failed to load Discord config');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    const changed = webhookUrl !== savedState.webhookUrl || enabled !== savedState.enabled;
    setHasChanges(changed);
  }, [webhookUrl, enabled, savedState]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      const token = localStorage.getItem('adminToken');
      if (!token) { onLogout(); return; }

      const response = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'set-discord-config',
          kanbanWebhookUrl: webhookUrl,
          kanbanNotificationsEnabled: enabled
        })
      });

      if (response.status === 401) { onLogout(); return; }

      const data = await response.json();
      if (data.success) {
        setMessage('Discord config saved successfully!');
        setSavedState({ webhookUrl, enabled });
        setHasChanges(false);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(data.error || 'Failed to save Discord config');
      }
    } catch (err) {
      setError('Failed to save Discord config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!webhookUrl.trim()) {
      setError('Enter a webhook URL first');
      return;
    }

    try {
      setTesting(true);
      setError('');
      setMessage('');
      const token = localStorage.getItem('adminToken');
      if (!token) { onLogout(); return; }

      const response = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'test-discord-webhook',
          webhookUrl: webhookUrl
        })
      });

      if (response.status === 401) { onLogout(); return; }

      const data = await response.json();
      if (data.success) {
        setMessage('Test message sent! Check your Discord channel.');
        setTimeout(() => setMessage(''), 5000);
      } else {
        setError(data.error || 'Failed to send test message');
      }
    } catch (err) {
      setError('Failed to send test message');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-retro-cyan text-lg">Loading Discord config...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-retro-cyan">Discord Notifications</h2>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {message && (
        <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded">
          {message}
        </div>
      )}

      <div className="retro-container p-4 retro-glow">
        <h3 className="text-lg font-semibold text-retro-cyan mb-4">Kanban Board Notifications</h3>
        <p className="text-gray-400 text-sm mb-4">
          Send Discord notifications when Kanban cards are created, moved to In Progress, or completed.
        </p>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setEnabled(!enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                enabled ? 'bg-retro-cyan' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-300">
              {enabled ? 'Notifications enabled' : 'Notifications disabled'}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Discord Webhook URL
            </label>
            <input
              type="text"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://discord.com/api/webhooks/..."
              className="w-full bg-retro-bg border border-gray-600 rounded px-3 py-2 text-gray-200 placeholder-gray-500 focus:border-retro-cyan focus:outline-none text-sm"
            />
            <p className="text-gray-500 text-xs mt-1">
              Create a webhook in your Discord server's channel settings under Integrations.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleTest}
              disabled={testing || !webhookUrl.trim()}
              className={`px-4 py-2 rounded font-semibold text-sm transition-all ${
                testing || !webhookUrl.trim()
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-600 text-gray-200 hover:bg-gray-500'
              }`}
            >
              {testing ? 'Sending...' : 'Send Test'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`px-4 py-2 rounded font-semibold text-sm transition-all ${
                saving || !hasChanges
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-retro-cyan text-retro-bg hover:brightness-110'
              }`}
            >
              {saving ? 'Saving...' : 'Save Config'}
            </button>
          </div>
        </div>
      </div>

      <div className="retro-container p-4 retro-glow">
        <h3 className="text-lg font-semibold text-retro-cyan mb-3">What gets posted</h3>
        <div className="space-y-3 text-sm text-gray-400">
          <div className="flex items-start gap-2">
            <span className="text-green-400 mt-0.5">&#9632;</span>
            <div>
              <span className="text-gray-300 font-medium">New Card Created</span>
              <span className="ml-1">When any Kanban card is added to the board</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">&#9632;</span>
            <div>
              <span className="text-gray-300 font-medium">Task In Progress</span>
              <span className="ml-1">When a card is moved to the In Progress column</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-400 mt-0.5">&#9632;</span>
            <div>
              <span className="text-gray-300 font-medium">Task Completed</span>
              <span className="ml-1">When a card is moved to the Done column</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
